// Thin typed fetch client. The API is a separate origin
// (api.moneyflow.grabbitfund.com); the BFF session cookie is scoped to
// `.moneyflow.grabbitfund.com`, so every call uses credentials: 'include' and
// every write carries the CSRF header. No retries that hide a failing
// endpoint and no fallback data on error: a failure is shown as a failure.

export class ApiError extends Error {
  status: number;
  reason: string | null;
  /** 422 only: the failing field locations and short messages (never values). */
  fields: { loc: string[]; msg: string }[];

  constructor(status: number, detail: string, reason: string | null, fields: { loc: string[]; msg: string }[] = []) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.reason = reason;
    this.fields = fields;
  }
}

/**
 * API origin. Production: https://api.moneyflow.grabbitfund.com. Local runs
 * set VITE_API_BASE_URL (e.g. http://127.0.0.1:8168; the API's DASHBOARD_ORIGIN
 * must then be this dev server's origin). Dev mock and `vite proxy` mode are
 * same-origin ('').
 */
export function resolveApiBase(): string {
  const configured = import.meta.env.VITE_API_BASE_URL as string | undefined;
  if (configured !== undefined && configured !== '') return configured.replace(/\/$/, '');
  return import.meta.env.DEV ? '' : 'https://api.moneyflow.grabbitfund.com';
}

const API_PREFIX = '/api/v1';
export const CSRF_HEADER = 'X-CSRF-Token';
/** The API's CSRF cookie (settings CSRF_COOKIE_NAME); GET /auth/me also returns the same value as `csrf`. */
export const CSRF_COOKIE = 'mf_csrf';
const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

let explicitCsrf: string | null = null;
export function setCsrfToken(token: string | null): void {
  explicitCsrf = token;
}

function readCsrfCookie(): string | null {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${CSRF_COOKIE}=([^;]+)`));
    return m?.[1] ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

type Listener = () => void;
const unauthorized = new Set<Listener>();
/** Called on any 401: the session provider drops the app to the login redirect. */
export function onUnauthorized(l: Listener): () => void {
  unauthorized.add(l);
  return () => unauthorized.delete(l);
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | readonly string[] | undefined | null>;
}

export function buildUrl(path: string, query?: RequestOptions['query']): string {
  const qs = new URLSearchParams();
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      if (typeof v === 'object') for (const x of v) qs.append(k, x); // repeatable parameter: ?source=a&source=b
      else qs.set(k, String(v));
    }
  }
  const s = qs.toString();
  return `${resolveApiBase()}${API_PREFIX}${path}${s ? `?${s}` : ''}`;
}

/** `returnTo` must be a same-origin RELATIVE path ("/deals/2?x=1"): the API refuses anything else. */
export function loginUrl(returnTo: string): string {
  return `${resolveApiBase()}${API_PREFIX}/auth/login?return_to=${encodeURIComponent(returnTo)}`;
}

/**
 * The browser's top-level hop to the Auth0 tenant logout. The API builds the target from its own
 * configuration (`https://<AUTH0_USER_DOMAIN>/v2/logout?client_id=...&returnTo=<dashboard origin>/`);
 * nothing from this page is sent along, so there is no open redirect to steer.
 */
export function providerLogoutUrl(): string {
  return `${resolveApiBase()}${API_PREFIX}/auth/logout-provider`;
}

/**
 * POST /auth/logout answers 303 (to the provider logout, or the dashboard) and
 * clears the cookies on that response. fetch cannot follow a cross-origin
 * redirect to another site, so it is not followed: an opaque redirect (or any
 * 2xx) means the server ended the session. A real error is thrown.
 */
export async function logoutRequest(): Promise<void> {
  const csrf = explicitCsrf ?? readCsrfCookie();
  const res = await fetch(`${resolveApiBase()}${API_PREFIX}/auth/logout`, {
    method: 'POST',
    credentials: 'include',
    redirect: 'manual',
    headers: csrf ? { [CSRF_HEADER]: csrf } : {},
  });
  if (res.type === 'opaqueredirect' || res.ok || (res.status >= 300 && res.status < 400)) return;
  let reason: string | null = null;
  try {
    const b = (await res.json()) as { reason?: unknown };
    reason = typeof b.reason === 'string' ? b.reason : null;
  } catch {
    /* no JSON body: the status alone is reported */
  }
  throw new ApiError(res.status, `Logout failed with status ${res.status}`, reason);
}

async function refreshCsrf(signal?: AbortSignal): Promise<boolean> {
  try {
    const res = await fetch(buildUrl('/auth/me'), { credentials: 'include', signal, headers: { Accept: 'application/json' } });
    const me = (await res.json()) as { authenticated?: boolean; csrf?: string | null };
    if (me.authenticated && me.csrf) {
      explicitCsrf = me.csrf;
      return true;
    }
  } catch {
    /* fall through: the original 403 is reported */
  }
  return false;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}, retried = false): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (UNSAFE.has(method)) {
    const csrf = explicitCsrf ?? readCsrfCookie();
    if (csrf) headers[CSRF_HEADER] = csrf;
  }
  const res = await fetch(buildUrl(path, opts.query), {
    method,
    credentials: 'include',
    signal: opts.signal,
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  if (res.status === 401) for (const l of unauthorized) l();

  let payload: unknown = null;
  try {
    payload = await res.json();
  } catch {
    throw new ApiError(res.status, res.ok ? 'Expected a JSON response.' : `Request failed with status ${res.status}`, res.ok ? 'non_json_response' : 'non_json_error');
  }
  if (!res.ok) {
    const b = (payload ?? {}) as Partial<{ detail: unknown; reason: unknown; errors: unknown }>;
    const fields = Array.isArray(b.errors)
      ? (b.errors as { loc?: unknown; msg?: unknown }[]).map((e) => ({ loc: Array.isArray(e.loc) ? e.loc.map(String) : [], msg: String(e.msg ?? '') }))
      : [];
    const detail = typeof b.detail === 'string' ? b.detail : `Request failed with status ${res.status}`;
    const reason = typeof b.reason === 'string' ? b.reason : null;
    // A stale CSRF token (session re-issued in another tab): re-read it once and retry.
    if (res.status === 403 && reason === 'csrf' && UNSAFE.has(method) && !retried && (await refreshCsrf(opts.signal))) {
      return apiFetch<T>(path, opts, true);
    }
    throw new ApiError(res.status, detail, reason, fields);
  }
  return payload as T;
}
